import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';

/**
 * Shows a dismissible "new version available" banner instead of silently
 * reloading the page mid-interaction (which the old window.confirm flow did).
 *
 * The service worker is configured with skipWaiting, so by the time this
 * banner appears the new assets are already cached and active — reloading
 * simply swaps the running app to the new version at a moment the user picks.
 *
 * Triggered by a 'pwa-update-available' window event dispatched from
 * src/index.js when the service worker reports an update.
 */
const PWAUpdatePrompt = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const handleUpdate = () => setShow(true);
    window.addEventListener('pwa-update-available', handleUpdate);
    return () => window.removeEventListener('pwa-update-available', handleUpdate);
  }, []);

  const handleReload = () => {
    setShow(false);
    window.location.reload();
  };

  if (!show) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto md:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50 animate-slide-up">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
            <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
              Update available
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              A new version is ready. Reload to get the latest improvements.
            </p>
          </div>
        </div>
        <button
          onClick={() => setShow(false)}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
        </button>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleReload}
          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors"
        >
          Reload
        </button>
        <button
          onClick={() => setShow(false)}
          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-md transition-colors"
        >
          Later
        </button>
      </div>
    </div>
  );
};

export default PWAUpdatePrompt;
